import React, { useEffect, useState } from "react";
import {
  Backdrop,
  Box,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BlockIcon from "@mui/icons-material/Block";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { collection, deleteDoc, doc, getDocs, setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import { format, isBefore, startOfDay } from "date-fns";

const TIME_SLOTS = ["11am - 1pm", "1pm - 3pm", "4pm - 6pm", "6pm - 8pm"];
const BRAND = "#194049";
const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// ── Helpers ───────────────────────────────────────────────────────────────────
function toDateStr(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function displayDate(dateStr) {
  try { return format(new Date(dateStr + "T12:00:00"), "MMMM d, yyyy"); }
  catch { return dateStr; }
}

// ── Slot picker modal ─────────────────────────────────────────────────────────
function SlotModal({ dateStr, slots, onSlots, onSave, onDelete, onClose, saving }) {
  const allSelected = slots.includes("all");
  const noSlots = slots.length === 0;

  const toggleAll = () => onSlots(allSelected ? [] : ["all"]);

  const toggleSlot = (slot) => {
    if (allSelected) return;
    onSlots((prev) =>
      prev.includes(slot) ? prev.filter((s) => s !== slot) : [...prev, slot]
    );
  };

  return (
    <Backdrop
      open
      onClick={onClose}
      sx={{ zIndex: (theme) => theme.zIndex.drawer + 2, alignItems: "flex-start", pt: { xs: 4, md: 8 } }}
    >
      <Paper
        onClick={(e) => e.stopPropagation()}
        elevation={4}
        sx={{ width: { xs: "92vw", sm: 440 }, borderRadius: "16px", p: 0, outline: "none" }}
      >
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2, borderBottom: "1px solid #e5e7eb", borderRadius: "16px 16px 0 0" }}>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
              Set Availability
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.25 }}>
              {displayDate(dateStr)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={onClose} sx={{ color: "#6b7280" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ p: 3 }}>
          {/* Block all toggle */}
          <Box sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#374151", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1.5 }}>
              Block entire day
            </Typography>
            <Box
              onClick={toggleAll}
              sx={{
                display: "inline-flex", alignItems: "center", gap: 0.75,
                px: 2.5, py: 1, borderRadius: "8px", border: "2px solid",
                borderColor: allSelected ? "#ef4444" : "#e5e7eb",
                bgcolor: allSelected ? "#fef2f2" : "#fff",
                color: allSelected ? "#ef4444" : "#374151",
                fontSize: 13, fontWeight: 600, cursor: "pointer",
                transition: "all 0.15s", userSelect: "none",
              }}
            >
              <BlockIcon sx={{ fontSize: 16 }} />
              {allSelected ? "Full day blocked" : "Block all day"}
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }}>
            <Typography sx={{ fontSize: 11, color: "#9ca3af", px: 1 }}>OR select specific slots</Typography>
          </Divider>

          {/* Individual time slots */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 3 }}>
            {TIME_SLOTS.map((slot) => {
              const active = allSelected || slots.includes(slot);
              return (
                <Box
                  key={slot}
                  onClick={() => toggleSlot(slot)}
                  sx={{
                    px: 2, py: 0.875, borderRadius: "8px", border: "1.5px solid",
                    borderColor: active ? "#ef4444" : "#e5e7eb",
                    bgcolor: active ? "#fef2f2" : allSelected ? "#f9fafb" : "#fff",
                    color: active ? "#ef4444" : "#374151",
                    fontSize: 13, fontWeight: 500,
                    cursor: allSelected ? "default" : "pointer",
                    opacity: allSelected && !slots.includes(slot) ? 0.5 : 1,
                    transition: "all 0.15s", userSelect: "none",
                  }}
                >
                  {slot}
                </Box>
              );
            })}
          </Box>

          {/* Warning */}
          {!noSlots && (
            <Box sx={{ bgcolor: "#fff7ed", border: "1px solid #fed7aa", borderRadius: "8px", px: 2, py: 1.5, mb: 3 }}>
              <Typography sx={{ fontSize: 12, color: "#92400e" }}>
                Blocked slots will be unavailable for customers to book.
              </Typography>
            </Box>
          )}

          {/* Actions */}
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "space-between" }}>
            <Box
              onClick={onDelete}
              sx={{ px: 2.5, py: 1, fontSize: 13, fontWeight: 600, borderRadius: "8px", border: "1px solid #e5e7eb", cursor: "pointer", color: "#9ca3af", bgcolor: "#fff", "&:hover": { color: "#ef4444", borderColor: "#fecaca" } }}
            >
              Clear
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box
                onClick={onClose}
                sx={{ px: 2.5, py: 1, fontSize: 13, fontWeight: 600, borderRadius: "8px", border: "1px solid #e5e7eb", cursor: "pointer", color: "#374151", bgcolor: "#fff", "&:hover": { bgcolor: "#f9fafb" } }}
              >
                Cancel
              </Box>
              <Box
                onClick={onSave}
                sx={{ px: 2.5, py: 1, fontSize: 13, fontWeight: 600, borderRadius: "8px", cursor: "pointer", color: "#fff", bgcolor: saving ? "#9ca3af" : BRAND, "&:hover": { bgcolor: saving ? "#9ca3af" : "#0f2a31" }, display: "flex", alignItems: "center", gap: 0.75 }}
              >
                {saving && <CircularProgress size={12} sx={{ color: "#fff" }} />}
                Save
              </Box>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Backdrop>
  );
}

// ── Mini calendar ─────────────────────────────────────────────────────────────
function MiniCalendar({ busyDays, onDateClick }) {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());

  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => {
    if (month === 0) { setMonth(11); setYear((y) => y - 1); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 11) { setMonth(0); setYear((y) => y + 1); }
    else setMonth((m) => m + 1);
  };

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  // pad to complete last row
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr = toDateStr(today.getFullYear(), today.getMonth(), today.getDate());

  return (
    <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", p: { xs: 2, md: 3 }, mb: 3 }}>
      {/* Month navigation */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <IconButton size="small" onClick={prevMonth} sx={{ color: BRAND }}>
          <ChevronLeftIcon />
        </IconButton>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>
          {format(new Date(year, month, 1), "MMMM yyyy")}
        </Typography>
        <IconButton size="small" onClick={nextMonth} sx={{ color: BRAND }}>
          <ChevronRightIcon />
        </IconButton>
      </Box>

      {/* Day-of-week headers */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
        {DAY_LABELS.map((d) => (
          <Typography key={d} sx={{ textAlign: "center", fontSize: 11, fontWeight: 600, color: "#9ca3af", pb: 1, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            {d}
          </Typography>
        ))}
      </Box>

      {/* Day cells */}
      <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 0.5 }}>
        {cells.map((day, idx) => {
          if (!day) return <Box key={`empty-${idx}`} />;
          const dateStr = toDateStr(year, month, day);
          const isBlocked = Boolean(busyDays[dateStr]);
          const isToday = dateStr === todayStr;
          const isPast = isBefore(new Date(dateStr + "T12:00:00"), startOfDay(new Date()));

          return (
            <Box
              key={dateStr}
              onClick={() => !isPast && onDateClick(dateStr)}
              sx={{
                aspectRatio: "1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "8px",
                fontSize: 13,
                fontWeight: isToday ? 700 : 400,
                cursor: isPast ? "default" : "pointer",
                position: "relative",
                bgcolor: isBlocked ? "#fef2f2" : isToday ? "#f0fdf4" : "transparent",
                color: isBlocked ? "#ef4444" : isPast ? "#d1d5db" : isToday ? BRAND : "#374151",
                border: isToday ? `2px solid ${BRAND}` : "2px solid transparent",
                "&:hover": isPast ? {} : { bgcolor: isBlocked ? "#fee2e2" : "#f3f4f6" },
                transition: "background 0.12s",
              }}
            >
              {day}
              {isBlocked && (
                <Box sx={{ position: "absolute", bottom: 3, left: "50%", transform: "translateX(-50%)", width: 4, height: 4, borderRadius: "50%", bgcolor: "#ef4444" }} />
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function AdminAvailability() {
  const [busyDays, setBusyDays] = useState({});
  const [loading, setLoading] = useState(true);
  const [editDate, setEditDate] = useState(null);
  const [editSlots, setEditSlots] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getDocs(collection(db, "availability"))
      .then((snap) => {
        const map = {};
        snap.docs.forEach((d) => { map[d.id] = d.data().slots || []; });
        setBusyDays(map);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const openDate = (dateStr) => {
    setEditSlots([...(busyDays[dateStr] || [])]);
    setEditDate(dateStr);
  };

  const handleSave = async () => {
    if (!editDate) return;
    setSaving(true);
    try {
      if (editSlots.length === 0) {
        await deleteDoc(doc(db, "availability", editDate));
        setBusyDays((prev) => { const n = { ...prev }; delete n[editDate]; return n; });
      } else {
        await setDoc(doc(db, "availability", editDate), { slots: editSlots, updatedAt: new Date().toISOString() });
        setBusyDays((prev) => ({ ...prev, [editDate]: editSlots }));
      }
      setEditDate(null);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editDate) return;
    setSaving(true);
    try {
      await deleteDoc(doc(db, "availability", editDate));
      setBusyDays((prev) => { const n = { ...prev }; delete n[editDate]; return n; });
      setEditDate(null);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const upcomingBlocked = Object.entries(busyDays)
    .filter(([date]) => !isBefore(new Date(date + "T12:00:00"), startOfDay(new Date())))
    .sort(([a], [b]) => a.localeCompare(b));

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
        <CircularProgress sx={{ color: BRAND }} />
      </Box>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 14, color: "#6b7280", mb: 3 }}>
        Click any date to block time slots. Blocked slots will be unavailable for booking.
      </Typography>

      <MiniCalendar busyDays={busyDays} onDateClick={openDate} />

      {/* Upcoming blocked days list */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid #e5e7eb", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
            Blocked Days
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>
            {upcomingBlocked.length} upcoming
          </Typography>
        </Box>

        {upcomingBlocked.length === 0 ? (
          <Box sx={{ py: 6, textAlign: "center" }}>
            <Typography sx={{ fontSize: 14, color: "#9ca3af" }}>No blocked days — you're fully available</Typography>
          </Box>
        ) : (
          <Box>
            {upcomingBlocked.map(([date, slots]) => (
              <Box
                key={date}
                onClick={() => openDate(date)}
                sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2, borderBottom: "1px solid #f3f4f6", cursor: "pointer", "&:hover": { bgcolor: "#fef2f2" }, transition: "background 0.15s", "&:last-child": { borderBottom: "none" } }}
              >
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>
                    {(() => { try { return format(new Date(date + "T12:00:00"), "EEE, MMM d, yyyy"); } catch { return date; } })()}
                  </Typography>
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 0.75 }}>
                    {slots.includes("all") ? (
                      <Chip label="All day blocked" size="small" sx={{ bgcolor: "#fef2f2", color: "#ef4444", fontSize: 11, height: 20, fontWeight: 600 }} />
                    ) : (
                      slots.map((s) => (
                        <Chip key={s} label={s} size="small" sx={{ bgcolor: "#fef2f2", color: "#ef4444", fontSize: 11, height: 20 }} />
                      ))
                    )}
                  </Box>
                </Box>
                <Typography sx={{ fontSize: 12, color: "#9ca3af" }}>Edit →</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {editDate && (
        <SlotModal
          dateStr={editDate}
          slots={editSlots}
          onSlots={setEditSlots}
          onSave={handleSave}
          onDelete={handleDelete}
          onClose={() => setEditDate(null)}
          saving={saving}
        />
      )}
    </Box>
  );
}
